import * as XLSX from 'xlsx';
import { getSubcoveragesTripExportByCompanyName } from '../services/api';

const formatExportCell = (value) => {
    if (value == null) return '';
    if (Array.isArray(value)) return value.join(', ');
    return value;
};

/**
 * Adresse combinée au format attendu ops :
 * activity_address,zip_code,city,country
 */
export const buildCombinedAddress = (source = {}) => {
    if (source.combined_address != null && String(source.combined_address).trim() !== '') {
        return String(source.combined_address).trim();
    }

    const activityAddress = source.activity_address == null ? '' : String(source.activity_address).trim();
    const zipCode = source.zip_code == null ? '' : String(source.zip_code).trim();
    const city = source.city == null ? '' : String(source.city).trim();
    const country = source.country == null ? '' : String(source.country).trim().toLowerCase();

    const parts = [activityAddress, zipCode, city, country].filter((value) => value !== '');
    return parts.join(',');
};

export const sanitizeFilenamePart = (name) =>
    String(name).trim().replace(/[\\/:*?"<>|]+/g, '_').replace(/\s+/g, '_');

export const parseFilenameFromContentDisposition = (header) => {
    if (!header) return null;
    const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(header);
    if (utf8Match?.[1]) {
        try {
            return decodeURIComponent(utf8Match[1]);
        } catch {
            return utf8Match[1];
        }
    }
    const match = /filename="?([^";]+)"?/i.exec(header);
    return match?.[1] || null;
};

const resolveCoverageType = (source = {}) => {
    if (source.coverage_type != null && String(source.coverage_type).trim() !== '') {
        return String(source.coverage_type).trim();
    }
    return '';
};

export const flattenSubcoveragesTripForExcel = (payload) => {
    if (Array.isArray(payload?.rows) && Array.isArray(payload?.columns)) {
        return payload.rows.map((row) => {
            const excelRow = {};
            payload.columns.forEach((column) => {
                excelRow[column] = formatExportCell(row[column]);
            });
            if (!Object.prototype.hasOwnProperty.call(excelRow, 'combined_address')) {
                excelRow.combined_address = buildCombinedAddress(row);
            } else if (!excelRow.combined_address) {
                excelRow.combined_address = buildCombinedAddress(row);
            }
            if (!Object.prototype.hasOwnProperty.call(excelRow, 'coverage_type')) {
                excelRow.coverage_type = resolveCoverageType(row);
            } else if (!excelRow.coverage_type) {
                excelRow.coverage_type = resolveCoverageType(row);
            }
            return excelRow;
        });
    }

    const list = Array.isArray(payload?.subcoverages_trip)
        ? payload.subcoverages_trip
        : Array.isArray(payload)
            ? payload
            : [];

    return list.map((item) => {
        const subcoverageTrip = item?.subcoverage_trip || item || {};
        const reference = item?.reference || {};
        const partner = item?.partner_info || {};
        const row = {};

        Object.keys(subcoverageTrip).forEach((key) => {
            row[key] = formatExportCell(subcoverageTrip[key]);
        });
        Object.keys(reference).forEach((key) => {
            row[`reference_${key}`] = formatExportCell(reference[key]);
        });
        Object.keys(partner).forEach((key) => {
            row[`partner_${key}`] = formatExportCell(partner[key]);
        });

        row.combined_address = buildCombinedAddress({
            ...subcoverageTrip,
            combined_address: subcoverageTrip.combined_address ?? item?.combined_address,
        });
        row.coverage_type = resolveCoverageType({
            ...subcoverageTrip,
            coverage_type: subcoverageTrip.coverage_type ?? item?.coverage_type,
        });

        return row;
    });
};

const ensureExportColumns = (columns) => {
    let next = Array.isArray(columns) ? [...columns] : [];
    if (!next.includes('combined_address')) {
        next = [...next, 'combined_address'];
    }
    if (!next.includes('coverage_type')) {
        next = [...next, 'coverage_type'];
    }
    return next;
};

const getExportColumns = (payload, rows) => {
    if (Array.isArray(payload?.columns) && payload.columns.length > 0) {
        return ensureExportColumns(payload.columns);
    }
    if (rows.length > 0) {
        return ensureExportColumns(Object.keys(rows[0]));
    }
    return ['combined_address', 'coverage_type'];
};

const defaultExportFilename = (companyName) =>
    `subcoverages_trip_${sanitizeFilenamePart(companyName)}.xlsx`;

const exportJsonPayloadToExcel = (json, filename) => {
    const rows = flattenSubcoveragesTripForExcel(json);
    if (!rows.length) {
        throw new Error('Aucune sous-couverture trip à exporter pour cette société.');
    }
    const columns = getExportColumns(json, rows);
    const worksheet = XLSX.utils.json_to_sheet(rows, { header: columns });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Subcoverages');
    XLSX.writeFile(workbook, filename);
};

/**
 * Exporte les subcoverages trip d'une société partenaire via GET /api/subcoverages-trip/export.
 * @param {string} companyName
 */
export const exportSubcoveragesTripByCompanyName = async (companyName) => {
    const trimmed = String(companyName || '').trim();
    if (!trimmed) {
        throw new Error('Veuillez sélectionner ou saisir un nom de société partenaire avant d\'exporter.');
    }

    const json = await getSubcoveragesTripExportByCompanyName(trimmed);
    exportJsonPayloadToExcel(json, defaultExportFilename(trimmed));
};
